/**
 * Location detail → Items tab. Inventory currently held at this location
 * (`/items?current_location_id=`) in a server-paginated DataTable with a
 * debounced search box. Reuses the shared inventory column set; rows link to the
 * equipment/component detail routes. Read-only.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Boxes } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { Input } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { useLocationItems, type Item } from '@/api/capacities';
import { paginationView } from '@/pages/customers/pagination-summary';
import { itemColumns, itemDetailPath } from '../../items/list-configs/item-column-definitions';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

const PAGE_SIZE = 20;

/** Debounce a fast-changing value (search box) to throttle server refetches. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function LocationItemsTab({ locationId }: { locationId: string }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);

  const { data, isLoading, isError, isFetching, refetch } = useLocationItems(locationId, {
    page,
    page_size: PAGE_SIZE,
    search: query || undefined,
  });

  const rows: Item[] = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const footer = useMemo(() => {
    const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
    return (
      <Pagination
        page={page}
        pageCount={view.pageCount}
        onPageChange={setPage}
        summary={`${view.summary}${isFetching ? ' …' : ''}`}
      />
    );
  }, [page, rows.length, total, pageCount, isFetching]);

  if (isError) return <TabError title="Failed to load items" onRetry={() => refetch()} />;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Input
            value={rawQuery}
            onChange={(e) => {
              setRawQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search items…"
            startSlot={<Search />}
            style={{ maxWidth: 280 }}
          />
        }
      />

      {isLoading ? (
        <TabLoading rows={6} />
      ) : (
        <DataTable
          columns={itemColumns}
          data={rows}
          getRowId={(r) => r.id}
          onRowClick={(r) => navigate(itemDetailPath(r))}
          emptyState={
            <EmptyState
              icon={<Boxes size={24} />}
              title="No items at this location"
              description="No inventory is currently held here."
            />
          }
          footer={footer}
        />
      )}
    </div>
  );
}
